import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, Button, Space } from 'antd';
import { PageHeader } from '../../components/common/PageHeader';
import { DataTable } from '../../components/common/DataTable';
import { StatusBadge } from '../../components/common/StatusBadge';
import { useInvoicesList, useBatchReprocess } from '../../hooks/useInvoices';
import dayjs from 'dayjs';
import type { AdminInvoiceListItem } from '../../types/invoices';
import type { ColumnsType } from 'antd/es/table';

const STATUS_COLORS: Record<string, string> = { completed: 'green', pending: 'blue', processing: 'orange', failed: 'red' };

export function InvoicesListPage() {
  const navigate = useNavigate();
  const [params, setParams] = useState({ page: 1, per_page: 20, search: '', status: undefined as string | undefined });
  const [selected, setSelected] = useState<string[]>([]);
  const { data, isLoading } = useInvoicesList(params);
  const batchReprocess = useBatchReprocess();

  const columns: ColumnsType<AdminInvoiceListItem> = [
    { title: 'Filename', dataIndex: 'original_filename', key: 'filename', render: (v: string, r) => <a onClick={() => navigate(`/invoices/${r.id}`)}>{v}</a> },
    { title: 'Organization', dataIndex: 'organization_name', key: 'org' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (v: string) => <StatusBadge status={v} colorMap={STATUS_COLORS} /> },
    { title: 'Created', dataIndex: 'created_at', key: 'created', render: (v: string) => dayjs(v).format('MMM D, YYYY HH:mm') },
  ];

  return (
    <>
      <PageHeader title="Invoices" subtitle={`${data?.total ?? 0} total`} />
      <DataTable<AdminInvoiceListItem>
        columns={columns} dataSource={data?.data} loading={isLoading} total={data?.total} page={params.page} perPage={params.per_page}
        onPageChange={(p, ps) => setParams((s) => ({ ...s, page: p, per_page: ps }))}
        onSearch={(v) => setParams((s) => ({ ...s, search: v, page: 1 }))}
        rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) }}
        extra={
          <Space>
            <Select placeholder="Status" allowClear style={{ width: 130 }} onChange={(v) => setParams((s) => ({ ...s, status: v, page: 1 }))} options={['pending', 'processing', 'completed', 'failed'].map((s) => ({ label: s, value: s }))} />
            {selected.length > 0 && <Button type="primary" loading={batchReprocess.isPending} onClick={() => batchReprocess.mutate({ invoice_ids: selected })}>Reprocess Selected ({selected.length})</Button>}
          </Space>
        }
      />
    </>
  );
}
